import { useId } from "react";
import { FormatterMode, parseFormatterMode } from "@textformatter/formatter";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import type { FormatterMode as FormatterModeValue, FormatterOptions, FormatterTransforms } from "./types";

type FormatterControlsProps = {
  mode: FormatterModeValue;
  onModeChange: (mode: FormatterModeValue) => void;
  options: FormatterOptions;
  onChange: (next: FormatterOptions) => void;
  transforms: FormatterTransforms;
  onTransformsChange: (next: FormatterTransforms) => void;
  showOptionDescriptions?: boolean;
  showFieldLabels?: boolean;
};

function OptionCheckbox({
  id,
  label,
  checked,
  onChange,
}: {
  id: string;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label htmlFor={id} className="flex items-center gap-2 text-xs text-slate-700">
      <input
        id={id}
        type="checkbox"
        className="h-4 w-4 rounded border-slate-300 accent-orange-500"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>{label}</span>
    </label>
  );
}

export default function FormatterControls({
  mode,
  onModeChange,
  options,
  onChange,
  transforms,
  onTransformsChange,
  showOptionDescriptions = true,
  showFieldLabels = true,
}: FormatterControlsProps) {
  const idPrefix = useId();
  const isTextMode = mode === FormatterMode.Text;

  function updateCaseTransform(next: "uppercase" | "lowercase" | "capitalize", checked: boolean) {
    if (!checked) {
      onTransformsChange({ ...transforms, [next]: false });
      return;
    }
    onTransformsChange({
      ...transforms,
      uppercase: next === "uppercase",
      lowercase: next === "lowercase",
      capitalize: next === "capitalize",
    });
  }

  return (
    <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50 p-3">
      {showOptionDescriptions ? (
        <>
          <p className="text-sm font-medium text-slate-900">Formatting options</p>
          <p className="mt-1 text-xs text-slate-600">
            Choose a mode, then fine-tune options. Changes are remembered per mode.
          </p>
        </>
      ) : null}

      <div className={showOptionDescriptions ? "mt-3 grid gap-3 sm:grid-cols-2" : "grid gap-3 sm:grid-cols-2"}>
        <div>
          {showFieldLabels ? <p className="mb-1 text-xs font-medium text-slate-700">Mode</p> : null}
          <Select
            value={mode}
            onValueChange={(value) => onModeChange(parseFormatterMode(value, FormatterMode.Text))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Mode" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={FormatterMode.Text}>Text</SelectItem>
              <SelectItem value={FormatterMode.Web}>Web</SelectItem>
              <SelectItem value={FormatterMode.Ecommerce}>Ecommerce</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {!isTextMode ? (
          <div>
            {showFieldLabels ? (
              <p className="mb-1 text-xs font-medium text-slate-700">Heading style</p>
            ) : null}
            <Select
              value={options.headingStyle}
              onValueChange={(value) =>
                onChange({ ...options, headingStyle: value === "h2" ? "h2" : "bold" })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Heading style" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bold">Headings as bold text</SelectItem>
                <SelectItem value="h2">Headings as H2</SelectItem>
              </SelectContent>
            </Select>
          </div>
        ) : null}
      </div>

      {isTextMode ? (
        <>
          {showFieldLabels ? <p className="mt-3 mb-1 text-xs font-medium text-slate-700">Text transformations</p> : null}
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            <OptionCheckbox
              id={`${idPrefix}-transform-strip-html`}
              label="Strip HTML"
              checked={Boolean(transforms.stripHtml)}
              onChange={(checked) => onTransformsChange({ ...transforms, stripHtml: checked })}
            />
            <OptionCheckbox
              id={`${idPrefix}-transform-fix-spacing`}
              label="Fix spacing"
              checked={Boolean(transforms.normalizeWhitespace)}
              onChange={(checked) =>
                onTransformsChange({ ...transforms, normalizeWhitespace: checked })
              }
            />
            <OptionCheckbox
              id={`${idPrefix}-transform-uppercase`}
              label="Uppercase"
              checked={Boolean(transforms.uppercase)}
              onChange={(checked) => updateCaseTransform("uppercase", checked)}
            />
            <OptionCheckbox
              id={`${idPrefix}-transform-lowercase`}
              label="Lowercase"
              checked={Boolean(transforms.lowercase)}
              onChange={(checked) => updateCaseTransform("lowercase", checked)}
            />
            <OptionCheckbox
              id={`${idPrefix}-transform-capitalize`}
              label="Capitalize"
              checked={Boolean(transforms.capitalize)}
              onChange={(checked) => updateCaseTransform("capitalize", checked)}
            />
          </div>
        </>
      ) : (
        <>
          {showFieldLabels ? <p className="mt-3 mb-1 text-xs font-medium text-slate-700">Text options</p> : null}
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            <OptionCheckbox
              id={`${idPrefix}-detect-headings`}
              label="Detect headings"
              checked={options.detectHeadings}
              onChange={(checked) => onChange({ ...options, detectHeadings: checked })}
            />
            <OptionCheckbox
              id={`${idPrefix}-detect-lists`}
              label="Detect lists"
              checked={options.detectLists}
              onChange={(checked) => onChange({ ...options, detectLists: checked })}
            />
            <OptionCheckbox
              id={`${idPrefix}-detect-colon-labels`}
              label='Detect "Label:" sections'
              checked={options.detectColonLabels}
              onChange={(checked) => onChange({ ...options, detectColonLabels: checked })}
            />
            {mode === FormatterMode.Ecommerce ? (
              <>
                <OptionCheckbox
                  id={`${idPrefix}-split-after-sentence-end`}
                  label="Split after sentence end"
                  checked={options.splitAfterSentenceEnd}
                  onChange={(checked) => onChange({ ...options, splitAfterSentenceEnd: checked })}
                />
                <OptionCheckbox
                  id={`${idPrefix}-nutrition-block-parsing`}
                  label="Nutrition block parsing"
                  checked={options.nutritionBlockParsing}
                  onChange={(checked) => onChange({ ...options, nutritionBlockParsing: checked })}
                />
              </>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}
